import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTES } from '../../constants';
import { authApi, writeStoredToken, writeStoredUser } from '../../services/authApi';
import './OAuthCallbackPage.css';

/**
 * OAuthCallbackPage — Handles the redirect back from the backend after OAuth.
 *
 * The backend redirects here with query params:
 *   ?token=JWT&isNewUser=true|false&provider=twitch|google
 *   ?error=ERROR_CODE&message=...
 *
 * Routes:
 *   - New user → / with state { step: 'OAUTH_USERNAME', token, provider }
 *   - Existing user → / with state { step: 'SUCCESS' }
 *   - Error → / with error state
 */
function OAuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');

  useEffect(() => {
    sessionStorage.removeItem('oauth_redirecting');

    let cancelled = false;

    const completeOAuth = async () => {
      const token = searchParams.get('token');
      const error = searchParams.get('error');
      const isNewUser = searchParams.get('isNewUser') === 'true';
      const provider = searchParams.get('provider');

      if (error) {
        setStatus('error');
        setTimeout(() => navigate(ROUTES.WELCOME, { state: { error } }), 2000);
        return;
      }

      if (!token) {
        setStatus('error');
        setTimeout(() => navigate(ROUTES.WELCOME), 2000);
        return;
      }

      writeStoredToken(token);

      try {
        const response = await authApi.getCurrentUser(token);
        if (!cancelled && response?.user) {
          writeStoredUser(response.user);
        }
      } catch (loadError) {
        console.error('Failed to hydrate current user after OAuth callback:', loadError);
      }

      if (cancelled) return;

      if (isNewUser) {
        navigate(ROUTES.WELCOME, {
          state: { step: 'OAUTH_USERNAME', token, provider },
        });
      } else {
        navigate(ROUTES.WELCOME, { state: { step: 'SUCCESS' } });
      }
    };

    completeOAuth();

    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate]);

  return (
    <div className="oauth-callback">
      {status === 'processing' && (
        <>
          <div className="oauth-callback__spinner" />
          <p className="oauth-callback__text">Completing authentication...</p>
        </>
      )}
      {status === 'error' && (
        <p className="oauth-callback__text oauth-callback__text--error">
          Authentication failed. Redirecting...
        </p>
      )}
    </div>
  );
}

export default OAuthCallbackPage;
